import { useMemo, useRef, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';

import isHotkey from 'is-hotkey';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils';
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { SEARCH_MENTIONS } from '@/lib/routes/apiEndpoints';
import { FACEBOOK } from '@/lib/socialProfile';

import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import Tooltip from '@/components/base/Tooltip';

const CommentInput = ({
    socialProfile,
    value,
    onChange,
    mentions,
    setMentions,
    setCursorPosition,
    disabled,
    comments,
    submitAction,
    placeholder,
    ...restProps
}) => {
    const { t } = useTranslation();
    const [mentionsRequest, setMentionsRequest] = useState({ status: 'idle' });
    const mentionsRequestCancelToken = useRef(undefined);

    const mentionUsers = useMemo(() => {
        let mentionUsers = [];

        if (comments && socialProfile?.social_network_type !== FACEBOOK) {
            comments.map((dataComment) => {
                dataComment.data.map((comment) => {
                    if (
                        comment?.data?.username &&
                        comment?.data?.username !== socialProfile.slug &&
                        mentionUsers.findIndex(
                            (mentionUser) => mentionUser.display === comment?.data?.username
                        ) < 0 &&
                        mentions.findIndex((mention) => mention.id === comment?.data?.username) < 0
                    ) {
                        mentionUsers.push({
                            id: comment?.data?.username,
                            display: comment?.data?.username
                        });
                    }
                });
            });
        }
        return mentionUsers;
    }, [comments, mentions, socialProfile]);

    let params = {
        displayTransform: (id, display) => `@${display}`,
        data: mentionUsers
    };

    if (socialProfile?.social_network_type === FACEBOOK) {
        params = {
            displayTransform: (id, display) => `${display}`,
            data: async (query, callback) => {
                if (!query) return;
                if (query.length < 3) return;
                if (typeof mentionsRequestCancelToken.current != typeof undefined) {
                    mentionsRequestCancelToken?.current?.abort();
                }
                mentionsRequestCancelToken.current = new AbortController();
                try {
                    setMentionsRequest({ status: 'pending' });
                    const results = await get(
                        injectEndpointUrlParams(SEARCH_MENTIONS, { SPID: socialProfile?.id }),
                        { search: query },
                        { signal: mentionsRequestCancelToken?.current?.signal }
                    );

                    const formattedData = [];

                    if (results) {
                        results.data?.data?.map((el) => {
                            if (
                                el?.id !== socialProfile.social_network_id &&
                                mentions.findIndex((mention) => mention.id === el?.id) < 0
                            ) {
                                formattedData.push({
                                    id: el?.id,
                                    display: el?.name,
                                    link: el?.link
                                });
                            }
                        });
                        setMentionsRequest({ status: 'idle' });
                    }

                    callback(formattedData);
                } catch (e) {
                    setMentionsRequest({ status: 'error' });
                }
            }
        };
    }

    return (
        <div className="relative">
            <MentionsInput
                disabled={disabled}
                value={value}
                onChange={(event, newValue, newPlainTextValue, mentions) => {
                    onChange(event.target.value);
                    setMentions(
                        mentions.map((mention) => {
                            return {
                                ...mention,
                                additionalCharacters:
                                    socialProfile?.social_network_type === FACEBOOK ? 5 : 4
                            };
                        })
                    );
                }}
                onBlur={(event) => {
                    const initialCursorPosition = event.nativeEvent.target.selectionStart;

                    const mentionsBeforeCaret = mentions?.filter(
                        (mention) => mention?.plainTextIndex <= initialCursorPosition
                    );

                    //get additionalCharacters to add to cursor position
                    const additionalCharacters = mentionsBeforeCaret?.reduce((acc, obj) => {
                        return acc + (obj.additionalCharacters + obj?.id?.length);
                    }, 0);

                    const cursorInMention = mentions?.find((mention) => {
                        return (
                            parseInt(mention?.plainTextIndex) <= initialCursorPosition &&
                            initialCursorPosition <=
                                parseInt(mention?.plainTextIndex) + mention?.display.length
                        );
                    });

                    if (cursorInMention) {
                        const newCursorPosition =
                            parseInt(cursorInMention.plainTextIndex) +
                            cursorInMention.display.length +
                            +additionalCharacters +
                            1; // +1 for the space after the mention
                        setCursorPosition(newCursorPosition);
                    } else {
                        //set new cursor position
                        setCursorPosition(initialCursorPosition + additionalCharacters);
                    }
                }}
                onKeyDown={(e) => {
                    // send form when ctrl+Enter or cmd+enter
                    if (isHotkey('mod+enter', e)) {
                        submitAction();
                    }
                }}
                className="mention-picker"
                placeholder={placeholder ?? t('commons:media_viewer.say_something')}
                allowSpaceInQuery={true}
                allowSuggestionsAboveCursor={true}
                {...restProps}>
                <Mention
                    appendSpaceOnAdd={true}
                    trigger="@"
                    className={socialProfile?.social_network_type === FACEBOOK ? 'mention' : ''}
                    markup="@[__id__][__display__]"
                    {...params}
                    renderSuggestion={(entry, search, highlightedDisplay, index, focused) => (
                        <div className="flex flex-col space-y-2 p-1">
                            <div
                                className={classNames('flex flex-col rounded-m px-2 py-2 text-sm', {
                                    'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20': focused
                                })}>
                                {highlightedDisplay}
                            </div>
                            {entry.link && (
                                <a
                                    className={classNames(
                                        'dark:text-icoDarkmode-wolf inline-flex items-center space-x-1 px-2 pb-2 text-xs text-icoGray-500 hover:text-icoBlue'
                                    )}
                                    href={entry?.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}>
                                    <Icon icon="link" className="text-xxs" />
                                    <span className="underline">{entry?.link}</span>
                                </a>
                            )}
                        </div>
                    )}
                />
            </MentionsInput>
            {mentionsRequest.status === 'pending' && (
                <Loader className="absolute right-1.5 top-1/2 -mt-2 h-4 w-4 text-icoBlue dark:text-icoBlue" />
            )}
            {mentionsRequest.status === 'error' && (
                <Tooltip content={t('errors:error_occured_fetch_data')}>
                    <Icon
                        icon="warning"
                        className="text-md absolute right-1.5 top-1/2 z-10 -mt-2 text-icoRed"
                    />
                </Tooltip>
            )}
        </div>
    );
};

export default CommentInput;
